import React from "react";
import BellIcon from "@heroicons/react/24/solid/BellIcon";
import Bars3Icon from "@heroicons/react/24/solid/Bars3Icon";
import MagnifyingGlassIcon from "@heroicons/react/24/solid/MagnifyingGlassIcon";

import {
  Avatar,
  Badge,
  Box,
  IconButton,
  Stack,
  SvgIcon,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { AccountPopover } from "./AccountPopover";
import { usePopover } from "src/hooks/use-popover";
import { useSelector } from "react-redux";
import { getAuthState } from "src/redux/slices/authSlice";
import { KeyboardArrowDown } from "@mui/icons-material";

interface TopNavProps {
  onNavOpen: () => void;
  sideNavWidth: number;
  topNavHeight: number;
}

function stringToColor(string: string) {
  let hash = 0;
  let i;

  /* eslint-disable no-bitwise */
  for (i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }

  let color = "#";

  for (i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }
  /* eslint-enable no-bitwise */

  return color;
}

function stringAvatar(name: string) {
  if (name.split(" ").length > 1) {
    return `${name.split(" ")[0][0]}${name.split(" ")[1][0]}`;
  }
  return `${name.split(" ")[0][0]}${name.split(" ")[0][1]}`;
}

export const TopNav: React.FC<TopNavProps> = (props) => {
  const { onNavOpen, sideNavWidth, topNavHeight } = props;
  const theme = useTheme();
  const lgUp: boolean = useMediaQuery(theme.breakpoints.up("lg"));
  const accountPopover = usePopover();

  const { userName } = useSelector(getAuthState);

  return (
    <>
      <Box
        component="header"
        sx={{
          backdropFilter: "blur(6px)",
          backgroundColor: (theme) =>
            alpha(theme.palette.background.paper, 0.8),
          position: "sticky",
          left: {
            lg: `${sideNavWidth}px`,
          },
          top: 0,
          width: {
            lg: `calc(100% - ${sideNavWidth}px)`,
          },
          zIndex: (theme) => theme.zIndex.appBar,
          boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2)",
        }}
      >
        <Stack
          alignItems="center"
          direction="row"
          justifyContent="space-between"
          spacing={2}
          sx={{
            minHeight: topNavHeight,
            px: 2,
          }}
        >
          <Stack alignItems="center" direction="row" spacing={2}>
            {!lgUp && (
              <IconButton onClick={onNavOpen}>
                <SvgIcon fontSize="small">
                  <Bars3Icon />
                </SvgIcon>
              </IconButton>
            )}
            {/* <Tooltip title="Search">
              <IconButton>
                <SvgIcon fontSize="small">
                  <MagnifyingGlassIcon />
                </SvgIcon>
              </IconButton>
            </Tooltip> */}
          </Stack>
          <Stack alignItems="center" direction="row" spacing={2}>
            <Tooltip title="Notifications">
              <IconButton>
                <Badge badgeContent={4} color="success" variant="dot">
                  <SvgIcon fontSize="small">
                    <BellIcon />
                  </SvgIcon>
                </Badge>
              </IconButton>
            </Tooltip>
            <Stack
              direction="row"
              spacing={1}
              onClick={accountPopover.handleOpen}
              ref={accountPopover.anchorRef}
              justifyContent="center"
              alignItems="center"
              sx={{ cursor: "pointer" }}
            >
              <Avatar
                sx={{
                  bgcolor: "primary",
                  height: 32,
                  width: 32,
                }}
                {...(userName
                  ? { children: stringAvatar(String(userName).toUpperCase()) }
                  : {})}
              />
              <Typography variant="h6" color="primary">
                {String(userName).toUpperCase()}
              </Typography>
              <SvgIcon color="primary">
                <KeyboardArrowDown />
              </SvgIcon>
            </Stack>
          </Stack>
        </Stack>
      </Box>
      <AccountPopover
        anchorEl={accountPopover.anchorRef.current}
        open={accountPopover.open}
        onClose={accountPopover.handleClose}
        username={userName || ""}
      />
    </>
  );
};
